import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { Badge, Button, Card, Col, Row } from "react-bootstrap";
import { faFileClipboard, faFileText, faMessage, faStar, faBirthdayCake } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import useApp from "../../utils/useApp";
import { Tooltip } from "react-tooltip";
import IconEmbeddedButton from "../ui/IconEmbeddedButton";
import CreateNoteModal from "../ui/CreateNoteModal";
import { fetchUserCaseGuardians, fetchUserCaseMembers } from "../../services/httpClient";
import { User } from "../../types/user";

interface CaseCardState {
  myCase: any;
  caseStatus: string;
  types: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  limitedAccess: boolean;
  caseFilter: string;
  firstMyCase?: boolean;
}

const CaseCard = ({
  myCase,
  caseStatus,
  types,
  setShowToast,
  setToastText,
  setToastBody,
  limitedAccess,
  caseFilter,
  firstMyCase = false,
}: CaseCardState) => {
  const { auth }: any = useApp();
  const [showCreateNote, setShowCreateNote] = useState<boolean>(false);
  const [activeFamily, setActiveFamily] = useState<User[]>([]);
  const [userToSave, setUserToSave] = useState<boolean>(false);

  let navigate = useNavigate();

  const editMyCase = (e: any) => {
    navigate(`/my-cases/${e.id}`);
  };

  const onClickAssessDue = (e: any) => {
    e.stopPropagation();
    navigate(`/my-cases/${myCase.id}?key=assessment`);
  };

  const onClickUnreadMessages = (e: any) => {
    e.stopPropagation();
    navigate(`/my-cases/${myCase.id}?key=messages`);
  };

  const onClickTaskReview = (e: any) => {
    e.stopPropagation();
    navigate(`/my-cases/${myCase.id}?key=goals`);
  };

  const onClickCreateNote = (e: any) => {
    e.stopPropagation();
    getFamily();
    setShowCreateNote(true);
  };

  const getFamily = () => {
    fetchUserCaseGuardians(myCase?.id, true)
      .then((res: any) => {
        var guards = res[0].data.map((g: any) => {
          const deleted = g.deleted_at ? true : false;
          const banned = g.is_banned === 1 ? true : false;

          return {
            id: g.id,
            type: "guardian",
            key: g.firstname + " " + g.lastname,
            name: g.firstname + " " + g.lastname,
            intake_at: g.intake_at,
            exit_at: g.exit_at,
            isBanned: banned,
            isDeleted: deleted,
          };
        });

        fetchUserCaseMembers(myCase?.id, true)
          .then((res: any) => {
            var membs = res[0].data.map((m: any) => {
              const deleted = m.deleted_at ? true : false;
              const banned = m.is_banned === 1 ? true : false;

              return {
                id: m.id,
                type: "member",
                key: m.firstname + " " + m.lastname,
                name: m.firstname + " " + m.lastname,
                intake_at: m.intake_at,
                exit_at: m.exit_at,
                isBanned: banned,
                isDeleted: deleted,
              };
            });

            var members = [...guards, ...membs];
            members.sort((a: any, b: any) => a.isBanned - b.isBanned);
            setActiveFamily(members.filter((i: any) => !i.isDeleted));
          })
          .catch((err: any) => {
            console.log(err);
          });
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const [isTruncated, setIsTruncated] = useState(false);
  const titleRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = titleRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [myCase.name]);

  // build list of birthday names in this case
  const birthdayNames: string[] = [];
  if (myCase?.family && Array.isArray(myCase.family)) {
    myCase.family.forEach((f: any) => {
      if (f?.is_birthday) {
        const name = f.user_name ?? f.member_name ?? (f.firstname && f.lastname ? `${f.firstname} ${f.lastname}` : (f.name ?? "Unknown"));
        birthdayNames.push(name);
      }
    });
  }
  if (myCase?.members && Array.isArray(myCase.members)) {
    myCase.members.forEach((m: any) => {
      if (m?.is_birthday) {
        const name = m.user_name ?? m.member_name ?? (m.firstname && m.lastname ? `${m.firstname} ${m.lastname}` : (m.name ?? "Unknown"));
        birthdayNames.push(name);
      }
    });
  }
  const birthdayTooltip = birthdayNames.length ? `Birthday Today!\n${birthdayNames.join(", ")}` : "";

  return (
    <>
      <Card
        data-testid={firstMyCase ? "my-first-case-card" : `${myCase.id}`}
        className={`${caseStatus === "active" ? `card-cases` : `card-cases-inactive`} mx-2 mt-3 button ${
          !limitedAccess && `cursor-pointer`
        }`}
        onClick={(e) => {
          !limitedAccess && editMyCase(myCase);
        }}
      >
        <Card.Body>
          <Card.Title
            className={"mt-0 mb-0 app-header-text text-truncate"}
            ref={titleRef}
            {...(isTruncated
              ? {
                  "data-tooltip-id": "casecard-title-tooltip",
                  "data-tooltip-content": myCase.name,
                }
              : {})}
          >
            {myCase.name}
          </Card.Title>
          <hr className="m-0" />
          <Row className="ps-2">
            <Col xs={12} className="p-0">
              {auth?.profile?.role !== "support" && caseStatus === "active" && (
                <>
                  <Button
                    data-tooltip-id="tooltip-goassess"
                    data-tooltip-content={"Go To Assessments"}
                    variant="link"
                    className={`text-decoration-none p-0 button-none ${limitedAccess && `cursor-default`}`}
                    onClick={(e) => !limitedAccess && onClickAssessDue(e)}
                  >
                    <Card.Text
                      className="mt-2 mb-0 card-alert-text"
                      style={
                        myCase.assessment_due && caseFilter === "assessment"
                          ? { color: "#DA7E39" }
                          : myCase.assessment_due
                            ? { color: "#000000" }
                            : { color: "#b1bfc4" }
                      }
                    >
                      <FontAwesomeIcon icon={faFileClipboard} /> Assessment Due
                    </Card.Text>
                  </Button>
                  {!limitedAccess && <Tooltip id="tooltip-goassess" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />}
                </>
              )}
              {caseStatus === "active" && (
                <>
                  <Button
                    data-tooltip-id="tooltip-gomessage"
                    data-tooltip-content={"Go To Messages"}
                    variant="link"
                    className={`text-decoration-none p-0 button-none text-truncate ${limitedAccess && `cursor-default`}`}
                    onClick={(e) => !limitedAccess && onClickUnreadMessages(e)}
                  >
                    <Card.Text
                      className="mt-0 mb-0 card-alert-text"
                      style={
                        myCase.new_messages && caseFilter === "messages"
                          ? { color: "#DA7E39" }
                          : myCase.new_messages
                            ? { color: "#000000" }
                            : { color: "#b1bfc4" }
                      }
                    >
                      <FontAwesomeIcon icon={faMessage} /> Unread Messages
                    </Card.Text>
                  </Button>
                  {!limitedAccess && <Tooltip id="tooltip-gomessage" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />}
                </>
              )}
              {auth?.profile?.role !== "support" && caseStatus === "active" && (
                <>
                  <Button
                    data-tooltip-id="tooltip-gogoals"
                    data-tooltip-content={"Go To Goals"}
                    variant="link"
                    className={`text-decoration-none p-0 button-none text-truncate ${limitedAccess && `cursor-default`}`}
                    onClick={(e) => !limitedAccess && onClickTaskReview(e)}
                  >
                    <Card.Text
                      className="mt-0 mb-0 card-alert-text"
                      style={
                        myCase.task_review && caseFilter === "task"
                          ? { color: "#DA7E39" }
                          : myCase.task_review
                            ? { color: "#000000" }
                            : { color: "#b1bfc4" }
                      }
                    >
                      <FontAwesomeIcon icon={faStar} /> Next Task Review
                    </Card.Text>
                  </Button>
                  {!limitedAccess && <Tooltip id="tooltip-gogoals" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />}
                </>
              )}
            </Col>
          </Row>
          {auth?.profile?.role !== "support" && (
            <Row className="position-absolute bottom-0 end-0">
              <Col className="text-end p-2 me-3">
                <IconEmbeddedButton onClickButton={(e: any) => onClickCreateNote(e)} tooltipText="Create Note" icon={faFileText} />
              </Col>
            </Row>
          )}
          <Row className="position-absolute bottom-0">
            <Col className="text-end p-2">
              {birthdayNames.length > 0 && (
                <>
                  <span data-tooltip-id="tooltip-birthday" data-tooltip-content={birthdayTooltip} className="me-2">
                    <FontAwesomeIcon icon={faBirthdayCake} className="app-birthday-text" />
                  </span>
                  <Tooltip
                    id="tooltip-birthday"
                    place="top"
                    style={{ padding: "5px 10px", zIndex: "100", whiteSpace: "pre-line", textAlign: "center" }}
                  />
                </>
              )}
              <Badge bg="secondary">{myCase?.primaryCM?.user_name}</Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <CreateNoteModal
        showModal={showCreateNote}
        setShowModal={setShowCreateNote}
        caseId={myCase?.id}
        name={myCase?.name}
        types={types}
        family={activeFamily}
        setUserToSave={setUserToSave}
        setShowToast={setShowToast}
        setToastText={setToastText}
        setToastBody={setToastBody}
        limitedAccess={limitedAccess}
      />
      <Tooltip id="casecard-title-tooltip" place="top" style={{ padding: "5px 10px", zIndex: "900" }} />
    </>
  );
};

export default CaseCard;
